import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { Check, X, Clock, Inbox, Eye, AlertTriangle, SlidersHorizontal } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/context/AuthContext";

function statusPillClass(status) {
  if (status === "approved") return "bg-emerald-100 text-emerald-700";
  if (status === "rejected") return "bg-red-100 text-red-700";
  return "bg-amber-100 text-amber-700";
}

function fmtAge(h) {
  if (h == null) return "";
  if (h < 1) return `${Math.max(1, Math.round(h * 60))}m`;
  if (h < 48) return `${Math.round(h)}h`;
  return `${Math.round(h / 24)}d`;
}

function SlaBadge({ sla, ageHours }) {
  if (!sla || sla === "ok") return null;
  const overdue = sla === "overdue";
  return (
    <span
      data-testid={`approval-sla-${sla}`}
      className={`stat-pill ml-2 ${overdue ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}
      title={`Pending ${fmtAge(ageHours)}`}
    >
      <AlertTriangle size={12} />{overdue ? "Overdue" : "Waiting"} {fmtAge(ageHours)}
    </span>
  );
}

export default function Approvals() {
  const { user, isSuper } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [detail, setDetail] = useState(null);
  const [actRemark, setActRemark] = useState("");
  const [slaCfg, setSlaCfg] = useState({ warn_hours: 4, overdue_hours: 24 });
  const [showSla, setShowSla] = useState(false);
  const [slaDraft, setSlaDraft] = useState({ warn_hours: 4, overdue_hours: 24 });

  const load = useCallback(() => api.get("/approvals", { params: { status: filter || undefined } }).then((r) => setItems(r.data)), [filter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/settings/sla").then((r) => { setSlaCfg(r.data); setSlaDraft(r.data); }).catch(() => {}); }, []);

  const saveSla = async () => {
    try {
      const r = await api.put("/settings/sla", { warn_hours: +slaDraft.warn_hours, overdue_hours: +slaDraft.overdue_hours });
      setSlaCfg(r.data); setSlaDraft(r.data); setShowSla(false);
      toast.success("SLA thresholds updated"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const act = async (id, action, remark) => {
    try { await api.post(`/approvals/${id}/${action}`, null, { params: remark ? { remark } : {} }); toast.success(action + "d"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const isAdmin = isSuper || user?.role === "admin";
  const isAuditor = user?.role === "auditor";
  const canAct = (r) => isAdmin || (isAuditor && r.target_collection === "inward_outward");
  const showActions = isAdmin || isAuditor;

  const diffKeys = (before, after) => {
    const keys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
    return [...keys].filter((k) => !["_id", "id", "effects", "created_at", "created_by", "updated_at", "updated_by"].includes(k));
  };

  return (
    <Layout title="Approvals" subtitle={showActions ? (isAuditor ? "Inward/Outward entries awaiting your approval" : "Pending edit/delete requests from operators") : "Your submitted edit requests"}>
      <Toaster richColors position="top-right" />
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {[["pending", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"], ["", "All"]].map(([v, l]) => (
          <button key={v || "all"} onClick={() => setFilter(v)} className={`px-4 py-2 rounded-lg text-sm font-semibold border ${filter === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>{l}</button>
        ))}
        {isSuper && (
          <button data-testid="approval-sla-settings-btn" onClick={() => { setSlaDraft(slaCfg); setShowSla((s) => !s); }} className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border border-slate-200 bg-white text-slate-700 hover:border-slate-400"><SlidersHorizontal size={15} /> SLA <span className="text-slate-400 font-normal">({slaCfg.warn_hours}h / {slaCfg.overdue_hours}h)</span></button>
        )}
      </div>
      {isSuper && showSla && (
        <div data-testid="approval-sla-panel" className="section-card mb-4 p-4 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Warn after (hours)</label>
            <input data-testid="sla-warn-input" type="number" min="0" step="0.5" value={slaDraft.warn_hours} onChange={(e) => setSlaDraft({ ...slaDraft, warn_hours: e.target.value })} className="w-32 border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Overdue after (hours)</label>
            <input data-testid="sla-overdue-input" type="number" min="0" step="0.5" value={slaDraft.overdue_hours} onChange={(e) => setSlaDraft({ ...slaDraft, overdue_hours: e.target.value })} className="w-32 border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <button data-testid="sla-save-btn" onClick={saveSla} className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-semibold">Save</button>
          <p className="text-xs text-slate-400 basis-full">Pending requests turn <span className="text-amber-600 font-semibold">amber</span> after the warn threshold and <span className="text-red-600 font-semibold">red</span> after the overdue threshold. Admins also get a daily push digest of overdue requests.</p>
        </div>
      )}
      <div className="section-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr><th className="text-left py-3 px-4">Requested</th><th className="text-left py-3 px-4">By</th><th className="text-left py-3 px-4">Action</th><th className="text-left py-3 px-4">Target</th><th className="text-left py-3 px-4">Reason</th><th className="text-left py-3 px-4">Status</th>{showActions && <th></th>}</tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={showActions ? 7 : 6} className="text-center py-10 text-slate-400"><Inbox size={32} className="mx-auto mb-2 opacity-40" />No requests.</td></tr>}
            {items.map((r) => (
              <tr key={r.id} className={`border-t border-slate-100 ${r.sla === "overdue" ? "bg-red-50/60" : ""}`}>
                <td className="py-2.5 px-4 text-xs text-slate-500">{r.created_at?.slice(0, 19).replace("T", " ")}</td>
                <td className="py-2.5 px-4">{r.requested_by}</td>
                <td className="py-2.5 px-4"><span className={`stat-pill ${r.action === "delete" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>{r.action}</span></td>
                <td className="py-2.5 px-4 text-xs font-mono text-slate-600">{r.target_collection} / {r.target_id?.slice(0, 8)}…</td>
                <td className="py-2.5 px-4 text-slate-600 max-w-xs truncate">{r.reason || "—"}</td>
                <td className="py-2.5 px-4 whitespace-nowrap">
                  <div className="flex items-center"><span className={`stat-pill ${statusPillClass(r.status)}`}>{r.status === "pending" && <Clock size={12} />}{r.status}</span><SlaBadge sla={r.sla} ageHours={r.age_hours} /></div>
                  {r.status !== "pending" && r.resolved_by && (
                    <div data-testid={`approval-resolver-${r.id}`} className="text-[11px] text-slate-400 mt-1">by {r.resolved_by} · {r.resolved_at?.slice(0, 16).replace("T", " ")}</div>
                  )}
                </td>
                {showActions && (
                  <td className="py-2.5 px-4 text-right whitespace-nowrap">
                    <button data-testid={`approval-view-${r.id}`} onClick={() => { setDetail(r); setActRemark(""); }} className="text-slate-400 hover:text-blue-600 p-1 mr-1"><Eye size={16} /></button>
                    {r.status === "pending" && canAct(r) && <>
                      <button data-testid={`approval-approve-${r.id}`} onClick={() => act(r.id, "approve")} className="bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 rounded mr-1"><Check size={14} /></button>
                      <button data-testid={`approval-reject-${r.id}`} onClick={() => act(r.id, "reject")} className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded"><X size={14} /></button>
                    </>}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="font-heading text-xl font-semibold">Change Request · {detail.action} {detail.target_collection}</h2>
              <button onClick={() => setDetail(null)} className="text-slate-400"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <div className="flex gap-6"><span className="text-slate-500">Requested by</span><b>{detail.requested_by}</b></div>
              <div className="flex gap-6"><span className="text-slate-500">Remark</span><b>{detail.reason || "—"}</b></div>
              {detail.status !== "pending" && (
                <div data-testid="approval-resolution-info" className={`rounded-lg p-3 text-sm ${detail.status === "approved" ? "bg-emerald-50 border border-emerald-200" : "bg-red-50 border border-red-200"}`}>
                  <div className="flex gap-6"><span className="text-slate-500 w-28">{detail.status === "approved" ? "Approved by" : "Rejected by"}</span><b>{detail.resolved_by || "—"}</b></div>
                  <div className="flex gap-6"><span className="text-slate-500 w-28">When</span><b>{detail.resolved_at ? detail.resolved_at.slice(0, 19).replace("T", " ") : "—"}</b></div>
                  {detail.resolution_remark ? <div className="flex gap-6"><span className="text-slate-500 w-28">Resolution note</span><b>{detail.resolution_remark}</b></div> : null}
                </div>
              )}
              {detail.action === "delete" ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">This request will <b>delete</b> the record below.</div>
              ) : null}
              <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="approval-diff">
                <table className="w-full">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="text-left py-2 px-3">Field</th><th className="text-left py-2 px-3">Before</th><th className="text-left py-2 px-3">{detail.action === "delete" ? "—" : "After (requested)"}</th></tr></thead>
                  <tbody>
                    {diffKeys(detail.before, detail.payload).map((k) => {
                      const b = detail.before ? detail.before[k] : undefined;
                      const a = detail.payload ? detail.payload[k] : undefined;
                      const changed = detail.action === "edit" && JSON.stringify(b) !== JSON.stringify(a);
                      return (
                        <tr key={k} className={`border-t border-slate-100 ${changed ? "bg-amber-50" : ""}`}>
                          <td className="py-1.5 px-3 font-medium">{k}</td>
                          <td className="py-1.5 px-3 font-mono text-slate-600">{b === undefined ? "—" : String(b)}</td>
                          <td className={`py-1.5 px-3 font-mono ${changed ? "text-amber-700 font-semibold" : "text-slate-600"}`}>{detail.action === "delete" ? "—" : (a === undefined ? "—" : String(a))}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t bg-slate-50">
              {canAct(detail) && detail.status === "pending" && (
                <div className="flex-1 mr-2"><input data-testid="approval-remark-input" value={actRemark} onChange={(e) => setActRemark(e.target.value)} placeholder="Remark (optional)" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
              )}
              {canAct(detail) && detail.status === "pending" && <>
                <button data-testid="approval-detail-reject" onClick={() => { act(detail.id, "reject", actRemark); setDetail(null); }} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold">Reject</button>
                <button data-testid="approval-detail-approve" onClick={() => { act(detail.id, "approve", actRemark); setDetail(null); }} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold">Approve</button>
              </>}
              <button onClick={() => setDetail(null)} className="px-4 py-2 border rounded-lg text-sm font-medium bg-white">Close</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
